import React, {
  MutableRefObject,
  Dispatch,
  SetStateAction,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as _ from 'lodash-es';

import { Loop } from '../../types';
import GC from '@mescius/spread-sheets';
import { useStateWithGetter } from '../../utils/useStateWithGetter';

type LoopView = Loop & { sheetId: number };

type LoopContextProps = {
  addLoop: (item: Omit<LoopView, 'loopId'>, loopId?: number) => void;
  deleteLoop: (loopId: number) => void;
  restoreLoops: (items: LoopView[]) => void;
  loops: LoopView[];
  loopsRef: MutableRefObject<LoopView[]>;
  draftLoopRange: GC.Spread.Sheets.Range | null;
  setDraftLoopRange: Dispatch<SetStateAction<GC.Spread.Sheets.Range | null>>;
};

export const LoopContext = createContext<LoopContextProps>({
  addLoop: () => {},
  deleteLoop: () => {},
  restoreLoops: () => {},
  loops: [],
  loopsRef: { current: [] },
  draftLoopRange: null,
  setDraftLoopRange: () => {},
});

export function LoopProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [loops, setLoops] = useState<LoopView[]>([]);

  // 指定作業中の明細範囲を一時的に保管しておくための入れ物
  const [draftLoopRange, setDraftLoopRange, getDraftLoopRange] =
    useStateWithGetter<GC.Spread.Sheets.Range | null>(null);

  // spreadjsにバインドした関数内で最新のloopsを参照するために、
  // loopsのrefバージョンを用意
  const loopsRef = useRef(loops);
  useEffect(() => {
    loopsRef.current = loops;
  }, [loops]);

  const addLoop = useCallback(
    (item: Omit<LoopView, 'loopId'>, loopId?: number) => {
      setLoops((prev) => [
        ...prev,
        {
          loopId: loopId ?? (_.maxBy(prev, 'loopId')?.loopId ?? 0) + 1,
          ...item,
        },
      ]);
    },
    []
  );

  const deleteLoop = useCallback((loopId: number) => {
    setLoops((prev) =>
      _.filter(prev, (prevItem) => prevItem.loopId !== loopId)
    );
  }, []);

  const restoreLoops = useCallback((items: LoopView[]) => {
    setLoops(items);
  }, []);

  return (
    <LoopContext.Provider
      value={{
        addLoop,
        deleteLoop,
        restoreLoops,
        loops,
        loopsRef,
        draftLoopRange,
        setDraftLoopRange,
      }}
    >
      {children}
    </LoopContext.Provider>
  );
}

/**
 * フロントエンド用のループとプロパティの紐づけデータ
 */
export const useLoop = () => {
  return useContext(LoopContext);
};
